import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical,
  ArrowUp,
  ArrowDown,
  Trash2,
  FileText,
  ChevronLeft,
  ChevronRight,
  Plus,
  BookmarkPlus,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { BLOCK_ICON } from '@/constants/blockIcons';
import { LIBRARY_MENU_CONTENT_CLASS, LibraryPageMenuItems } from '@/components/library/LibraryPageMenuItems';
import type { Block, Template } from '@/types';
import { BlockType as BT } from '@/types';
import { pageStyles, resolvePageStyleConfig, type PageStyle } from '@/theme/uboTheme';
import { inferQuotePageBrand } from '@/lib/quoteBranding';
import { firstLinePlainTextFromHtml } from '@/lib/blockTitleFromHtml';
import { cn } from '@/lib/utils';
import { A4_LANDSCAPE } from '@/constants/pageFormat';
import type { PageThumbnailContent } from '@/hooks/usePageThumbnails';

export const BLOCK_LABEL: Record<string, string> = {
  [BT.COVER]: 'Coverpagina',
  [BT.CONTENT_PAGE]: 'Content',
  [BT.TEAM]: 'Team',
  [BT.INVESTMENT]: 'Investering',
  [BT.PLAN_VAN_AANPAK]: 'Plan van Aanpak',
  [BT.SIGNATURE_PAGE]: 'Handtekening',
  [BT.FOOTER]: 'Sluiting',
  [BT.TEXT]: 'Tekst',
  [BT.TABLE]: 'Tabel',
  [BT.IMAGE]: 'Afbeelding',
};

const SIDEBAR_TITLE_MAX = 24;

/** `maxLength`: de paginalijst kort lange titels af; een naamvoorstel wil de hele titel. */
export function getBlockTitle(block: Block, maxLength = SIDEBAR_TITLE_MAX): string {
  const c = block.content as Record<string, unknown>;

  if (c.title && typeof c.title === 'string' && c.title.length > 0) {
    const clean = c.title.replace(/<[^>]*>/g, '').trim();
    if (clean.length > 0) {
      const isGenericPlanTitle =
        block.type === BT.PLAN_VAN_AANPAK && clean.toLowerCase() === 'plan van aanpak';
      if (!isGenericPlanTitle) {
        return clean.length > maxLength ? clean.slice(0, maxLength) + '…' : clean;
      }
    }
  }

  if (c.mainTitle && typeof c.mainTitle === 'string') return c.mainTitle;
  if (c.headline && typeof c.headline === 'string') return c.headline;

  if (block.type === BT.CONTENT_PAGE || block.type === BT.PLAN_VAN_AANPAK) {
    const html = c.content;
    if (typeof html === 'string' && html.trim()) {
      const derived = firstLinePlainTextFromHtml(html, maxLength);
      if (derived) return derived;
    }
  }

  return BLOCK_LABEL[block.type] || block.type;
}

function getBlockStyle(block: Block): PageStyle | null {
  const c = block.content as Record<string, unknown>;
  if (c.style && typeof c.style === 'string' && c.style in pageStyles) {
    return c.style as PageStyle;
  }
  return null;
}

function getBlockBgColor(block: Block, quoteBrand: 'ubo' | 'ubx'): string {
  const style = getBlockStyle(block);
  if (!style || style === 'default') return '#ffffff';
  const c = block.content as { theme?: string };
  const brand = (c.theme ?? quoteBrand) === 'ubx' ? 'ubx' : 'ubo';
  return resolvePageStyleConfig(style, brand).bgColor;
}

const THUMBNAIL_ASPECT = 21 / 29.7; // A4 landscape height/width

let previewStyles: CSSStyleSheet | undefined;

function getPreviewStyles() {
  if (!previewStyles) {
    previewStyles = new CSSStyleSheet();
    previewStyles.replaceSync(Array.from(document.styleSheets, (sheet) => {
      try {
        return Array.from(sheet.cssRules, (rule) => rule.cssText).join('\n');
      } catch {
        return '';
      }
    }).join('\n'));
  }
  return previewStyles;
}

function NativePagePreview({ page }: { page: PageThumbnailContent }) {
  const frame = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const element = frame.current;
    if (!element) return;
    const observer = new ResizeObserver(() => setWidth(element.clientWidth));
    observer.observe(element);
    setWidth(element.clientWidth);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const element = frame.current;
    if (!element || !width) return;
    // Keep copied page IDs, CSS and editor selectors separate from the live editor.
    const shadow = element.shadowRoot ?? element.attachShadow({ mode: 'open' });
    shadow.adoptedStyleSheets = [getPreviewStyles()];
    const content = document.createElement('div');
    content.className = 'tiptap-editor';
    content.setAttribute('inert', '');
    content.style.width = `${page.width}px`;
    content.style.height = `${A4_LANDSCAPE.contentHeight}px`;
    content.style.overflow = 'hidden';
    content.style.zoom = String(width / page.width);
    content.innerHTML = page.html;
    shadow.replaceChildren(content);
  }, [page, width]);

  return (
    <div
      ref={frame}
      aria-hidden="true"
      data-page-preview
      className="absolute inset-0 pointer-events-none select-none"
      style={{ backgroundColor: page.backgroundColor }}
    />
  );
}

function PageThumbnail({
  block,
  index,
  preview,
  quoteBrand,
}: {
  block: Block;
  index: number;
  preview?: PageThumbnailContent;
  quoteBrand: 'ubo' | 'ubx';
}) {
  const bgColor = getBlockBgColor(block, quoteBrand);
  const isDark = bgColor !== '#ffffff';
  const Icon = BLOCK_ICON[block.type] || FileText;

  return (
    <div
      className="relative w-full rounded-md overflow-hidden border border-gray-200/80 shadow-sm"
      style={{
        aspectRatio: `${1 / THUMBNAIL_ASPECT}`,
        backgroundColor: bgColor,
      }}
    >
      {preview ? (
        <NativePagePreview page={preview} />
      ) : (
        <div
          className={cn(
          'absolute inset-0 flex flex-col items-center justify-center gap-1',
            isDark ? 'text-white/40' : 'text-gray-300'
          )}
        >
          <Icon className="h-6 w-6" />
          <span className="text-[9px] font-medium uppercase tracking-wider">
            {BLOCK_LABEL[block.type] || block.type}
          </span>
        </div>
      )}
      <div
        className={cn(
        'absolute top-1 left-1.5 text-[9px] font-bold tabular-nums leading-none',
          'bg-black/40 text-white rounded px-1 py-0.5'
        )}
      >
        {index + 1}
      </div>
    </div>
  );
}

interface SortablePageItemProps {
  block: Block;
  index: number;
  isSelected: boolean;
  onSelect: (block: Block) => void;
  onDelete: (block: Block) => void;
  preview?: PageThumbnailContent;
  quoteBrand: 'ubo' | 'ubx';
  compact?: boolean;
  pageCount: number;
  onMove: (block: Block, direction: -1 | 1) => void;
  onSaveToLibrary?: (block: Block) => void;
}

function SortablePageItem({
  block,
  index,
  isSelected,
  onSelect,
  onDelete,
  preview,
  quoteBrand,
  compact = false,
  pageCount,
  onMove,
  onSaveToLibrary,
}: SortablePageItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: block.id,
  });

  // De gesleepte pagina blijft dekkend en ligt boven de rest, zodat je ziet waar hij landt.
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : undefined,
  };

  const title = getBlockTitle(block);
  const dragLabel = `Pagina ${index + 1}: ${title}. Versleep om de volgorde te wijzigen.`;

  if (compact) {
    const bgColor = getBlockBgColor(block, quoteBrand);
    return (
      <button
        type="button"
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        data-sidebar-page-id={block.id}
        aria-current={isSelected ? 'page' : undefined}
        aria-label={dragLabel}
        onClick={() => onSelect(block)}
        className={cn(
          'relative w-8 h-5 rounded border cursor-grab active:cursor-grabbing touch-none',
          isSelected ? 'ring-2 ring-primary/40 border-primary/30' : 'border-gray-200 hover:border-gray-300',
          isDragging && 'shadow-md'
        )}
        style={{ ...style, backgroundColor: bgColor }}
        title={`${index + 1}. ${title} · Versleep om te ordenen`}
      >
        <span className={cn('text-[7px] font-bold', bgColor !== '#ffffff' ? 'text-white/50' : 'text-gray-400')}>
          {index + 1}
        </span>
      </button>
    );
  }

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      aria-label={dragLabel}
      data-sidebar-page-id={block.id}
      aria-current={isSelected ? 'page' : undefined}
      style={style}
      className={cn(
        // Alleen kleuren animeren: een transition op transform laat de pagina achter de muis aan lopen.
        'group relative flex flex-col rounded-lg cursor-grab active:cursor-grabbing touch-none transition-colors duration-150',
        'p-1.5',
        isSelected
          ? 'bg-[#f1f0ff] ring-1 ring-[#0007ec]'
          : 'hover:bg-gray-50 ring-1 ring-transparent hover:ring-gray-200',
        isDragging && 'shadow-lg',
        isDragging && !isSelected && 'bg-background ring-gray-200'
      )}
      onClick={() => onSelect(block)}
    >
      <PageThumbnail
        block={block}
        index={index}
        preview={preview}
        quoteBrand={quoteBrand}
      />

      <div className="flex items-center gap-1 mt-1.5 px-0.5">
        <span className="shrink-0 text-gray-400" title="Versleep deze pagina om de volgorde te wijzigen">
          <GripVertical className="h-3 w-3" />
        </span>

        <span
          className={cn(
          'flex-1 min-w-0 truncate text-[11px] leading-tight',
            isSelected ? 'text-[#0007ec] font-medium' : 'text-gray-600'
          )}
        >
          {title}
        </span>

        <div
          className="flex shrink-0 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100"
          onPointerDown={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            className="p-1 text-gray-400 hover:text-primary disabled:opacity-30"
            aria-label={`Pagina ${index + 1} omhoog`}
            title="Pagina omhoog"
            disabled={index === 0}
            onClick={() => onMove(block, -1)}
          >
            <ArrowUp className="h-3 w-3" />
          </button>
          <button
            type="button"
            className="p-1 text-gray-400 hover:text-primary disabled:opacity-30"
            aria-label={`Pagina ${index + 1} omlaag`}
            title="Pagina omlaag"
            disabled={index === pageCount - 1}
            onClick={() => onMove(block, 1)}
          >
            <ArrowDown className="h-3 w-3" />
          </button>
          {onSaveToLibrary && (
            <button
              type="button"
              className="p-1 text-gray-400 hover:text-primary"
              aria-label={`Pagina ${index + 1} opslaan in de bibliotheek`}
              title="Opslaan in bibliotheek, om in andere offertes te hergebruiken"
              onClick={() => onSaveToLibrary(block)}
            >
              <BookmarkPlus className="h-3 w-3" />
            </button>
          )}
          <button
            type="button"
            className="p-1 text-gray-400 hover:text-red-500"
            aria-label={`Pagina ${index + 1} verwijderen`}
            title="Verwijderen"
            onClick={() => onDelete(block)}
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

interface PageListSidebarProps {
  blocks: Block[];
  selectedBlockId: string | null;
  onSelectBlock: (block: Block) => void;
  onReorder: (blockIds: string[]) => void;
  onDeleteBlock: (block: Block) => void;
  onInsertLibraryPage: (page: Template, afterBlockId: string) => void;
  onSaveToLibrary?: (block: Block) => void;
  thumbnails?: Record<string, PageThumbnailContent>;
  layoutConfig?: Record<string, unknown> | null;
}

function PageInsertButton({
  afterBlockId,
  pageNumber,
  onInsertLibraryPage,
}: {
  afterBlockId: string;
  pageNumber: number;
  onInsertLibraryPage: PageListSidebarProps['onInsertLibraryPage'];
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Pagina invoegen na pagina ${pageNumber}`}
          title={`Pagina invoegen na pagina ${pageNumber}`}
          className="absolute left-1/2 top-full z-10 -mt-2 flex h-6 w-6 -translate-x-1/2 items-center justify-center rounded bg-white text-gray-400 opacity-0 transition-opacity duration-150 group-hover/page:opacity-100 hover:bg-primary/5 hover:text-primary focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 data-[state=open]:opacity-100"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="right" align="start" className={cn('w-56', LIBRARY_MENU_CONTENT_CLASS)}>
        <LibraryPageMenuItems onSelect={(page) => onInsertLibraryPage(page, afterBlockId)} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const COLLAPSE_BREAKPOINT = 1280;

export default function PageListSidebar({
  blocks,
  selectedBlockId,
  onSelectBlock,
  onReorder,
  onDeleteBlock,
  onInsertLibraryPage,
  onSaveToLibrary,
  thumbnails = {},
  layoutConfig,
}: PageListSidebarProps) {
  const quoteBrand = inferQuotePageBrand(blocks, layoutConfig ?? null);
  const [collapsed, setCollapsed] = useState(() => window.innerWidth < COLLAPSE_BREAKPOINT);
  const expandedListRef = useRef<HTMLDivElement>(null);
  const collapsedListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!selectedBlockId) return;
    const run = () => {
      const root = collapsed ? collapsedListRef.current : expandedListRef.current;
      if (!root) return;
      const el = Array.from(root.querySelectorAll('[data-sidebar-page-id]')).find(
        (node) => node.getAttribute('data-sidebar-page-id') === selectedBlockId
      );
      if (el instanceof HTMLElement) {
        el.scrollIntoView({ block: 'nearest', behavior: 'instant' });
      }
    };
    requestAnimationFrame(run);
  }, [selectedBlockId, collapsed]);

  useEffect(() => {
    const handleResize = () => {
      setCollapsed(window.innerWidth < COLLAPSE_BREAKPOINT);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const movePage = useCallback(
    (blockId: string, newIndex: number) => {
      const oldIndex = blocks.findIndex((block) => block.id === blockId);
      if (oldIndex < 0 || newIndex < 0 || newIndex >= blocks.length || oldIndex === newIndex) return;
      const reordered = [...blocks];
      const [moved] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, moved);

      onReorder(reordered.map((b) => b.id));
    },
    [blocks, onReorder]
  );
  // Tijdens het slepen staan de invoegknoppen stil terwijl de pagina's verschuiven; verberg ze.
  const [sorting, setSorting] = useState(false);
  const handleDragEnd = (event: DragEndEvent) => {
    setSorting(false);
    const { active, over } = event;
    if (over) movePage(String(active.id), blocks.findIndex((block) => block.id === over.id));
  };
  const dndProps = {
    sensors,
    collisionDetection: closestCenter,
    onDragStart: () => setSorting(true),
    onDragCancel: () => setSorting(false),
    onDragEnd: handleDragEnd,
  };
  const moveBlock = (block: Block, direction: -1 | 1) =>
    movePage(block.id, blocks.findIndex((entry) => entry.id === block.id) + direction);

  if (collapsed) {
    return (
      <div
        className="shrink-0 w-12 border-r border-gray-200 bg-gray-50/50 flex flex-col h-full min-h-0 items-center pt-3"
        data-workspace-controls
      >
        <button
          onClick={() => setCollapsed(false)}
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors shrink-0"
          title="Pagina's tonen"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
        <div
          ref={collapsedListRef}
          className="mt-3 flex-1 min-h-0 w-full overflow-y-auto flex flex-col items-center gap-1.5 pb-2 scrollbar-thin"
        >
          <DndContext {...dndProps}>
            <SortableContext items={blocks.map((block) => block.id)} strategy={verticalListSortingStrategy}>
              {blocks.map((block, i) => (
                <div key={block.id} className="group/page relative flex w-full flex-col items-center">
                  <SortablePageItem
                    block={block}
                    index={i}
                    compact
                    pageCount={blocks.length}
                    isSelected={selectedBlockId === block.id}
                    onSelect={onSelectBlock}
                    onDelete={onDeleteBlock}
                    onMove={moveBlock}
                    quoteBrand={quoteBrand}
                  />
                  {!sorting && (
                    <PageInsertButton
                      afterBlockId={block.id}
                      pageNumber={i + 1}
                      onInsertLibraryPage={onInsertLibraryPage}
                    />
                  )}
                </div>
              ))}
            </SortableContext>
          </DndContext>
        </div>
      </div>
    );
  }

  return (
    <div
      className="shrink-0 w-[224px] border-r border-border bg-background flex flex-col overflow-hidden"
      data-workspace-controls
    >
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-200">
        <span className="text-xs font-semibold text-foreground">
          Pagina’s <span className="ml-2 font-normal text-muted-foreground">{blocks.length}</span>
        </span>
        <button
          onClick={() => setCollapsed(true)}
          className="p-1 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          title="Sidebar inklappen"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="px-3 pt-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex h-9 w-full items-center justify-center gap-2 rounded border border-border bg-white text-xs hover:bg-accent"
            >
              <Plus size={14} />
              Pagina toevoegen
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className={cn('w-56', LIBRARY_MENU_CONTENT_CLASS)}>
            <LibraryPageMenuItems
              onSelect={(page) => onInsertLibraryPage(page, selectedBlockId ?? blocks.at(-1)?.id ?? '')}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div
        ref={expandedListRef}
        className="flex-1 min-h-0 overflow-y-auto px-2 py-2 space-y-2 scrollbar-thin"
      >
        <DndContext {...dndProps}>
          <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
            {blocks.map((block, index) => (
              <div key={block.id} className="group/page relative">
                <SortablePageItem
                  block={block}
                  index={index}
                  isSelected={selectedBlockId === block.id}
                  onSelect={onSelectBlock}
                  onDelete={onDeleteBlock}
                  preview={thumbnails[block.id]}
                  quoteBrand={quoteBrand}
                  pageCount={blocks.length}
                  onMove={moveBlock}
                  onSaveToLibrary={onSaveToLibrary}
                />
                {!sorting && (
                  <PageInsertButton
                    afterBlockId={block.id}
                    pageNumber={index + 1}
                    onInsertLibraryPage={onInsertLibraryPage}
                  />
                )}
              </div>
            ))}
          </SortableContext>
        </DndContext>

        {blocks.length === 0 && (
          <div className="text-center text-xs text-gray-400 py-8">Geen pagina's</div>
        )}
      </div>
    </div>
  );
}
